import { TM } from "@litres-libs/tracker-manager";
import {
  hasWindow,
  getFromLocalStorage,
  saveToLocalStorage,
} from "@litres/utils";
import type React from "react";
import isEmpty from "lodash/isEmpty";
import type { SearchSuggestionSerializer } from "@litres-libs/types";
import type { ILastSearch, ISearchResultItem } from "./types";

/**
 * https://en.wikibooks.org/wiki/Algorithm_Implementation/Strings/Levenshtein_distance#JavaScript
 * Расстояние Левенштейна — метрика, измеряющая по модулю разность между двумя последовательностями символов.
 */
export const getEditDistance = (a: string, b: string): number => {
  if (a.length === 0) return b.length;
  if (b.length === 0) return a.length;

  const matrix = [];

  // increment along the first column of each row
  let i = 0;
  for (; i <= b.length; i++) {
    matrix[i] = [i];
  }

  // increment each column in the first row
  let j = 0;
  for (; j <= a.length; j++) {
    matrix[0][j] = j;
  }

  // Fill in the rest of the matrix
  for (i = 1; i <= b.length; i++) {
    for (j = 1; j <= a.length; j++) {
      if (b.charAt(i - 1) == a.charAt(j - 1)) {
        matrix[i][j] = matrix[i - 1][j - 1];
      } else {
        matrix[i][j] = Math.min(
          matrix[i - 1][j - 1] + 1, // substitution
          Math.min(
            matrix[i][j - 1] + 1, // insertion
            matrix[i - 1][j] + 1
          )
        ); // deletion
      }
    }
  }

  return matrix[b.length][a.length];
};

/**
 * https://en.wikibooks.org/wiki/Algorithm_Implementation/Strings/Longest_common_substring#JavaScript
 * Наибольшая общая подстрока — подстрока двух или более строк, имеющая максимальную длину.
 */
export const longestCommonSubstring = (a: string, b: string): number => {
  // init max value
  let longestCommonSubstring = 0;
  // init 2D array with 0
  const table: [number[]] = [[]];
  const len1 = a.length;
  const len2 = b.length;
  let row = 0;
  let col = 0;
  for (row = 0; row <= len1; row++) {
    table[row] = [];
    for (col = 0; col <= len2; col++) {
      table[row][col] = 0;
    }
  }
  // fill table
  let i = 0;
  let j = 0;
  for (i = 0; i < len1; i++) {
    for (j = 0; j < len2; j++) {
      if (a[i] === b[j]) {
        if (table[i][j] === 0) {
          table[i + 1][j + 1] = 1;
        } else {
          table[i + 1][j + 1] = table[i][j] + 1;
        }
        if (table[i + 1][j + 1] > longestCommonSubstring) {
          longestCommonSubstring = table[i + 1][j + 1];
        }
      } else {
        table[i + 1][j + 1] = 0;
      }
    }
  }
  return longestCommonSubstring;
};

/**
 * Получить результаты последнего поиска пользователя
 * UPD: Теперь вместо массива строк возвращается объект с полями value и server
 * Поле value содержит как раз поисковый запрос, а поле source содержит источник, откуда мы получили этот поисковый запрос
 * "server" - если мы запросили историю поиска с сервера, и "client" - если юзер недавно самостоятельно вбил
 */
export const getLastSearchResults = (): ILastSearch[] => {
  const lastSearchResults = getFromLocalStorage<ILastSearch[] | null>(
    "last_search"
  );

  return lastSearchResults && Array.isArray(lastSearchResults)
    ? getLastFiveSearchResults(lastSearchResults)
    : [];
};

/**
 * Получаем только 5 последних поисковых запросов пользователя
 * Раньше сохраняли список до 10 запросов(включительно), но после смены ui стали сохранять 5
 * чтобы не возникало багов и не хранить лишние значения оставляем только 5 последних запросов
 * TODO: если коду на момент прочтения больше года-двух, можно подумать над тем, чтобы его удалить. К этому моменту у всех пользователей раннее сохраненный массив из 10 элементов должен обновиться до 5
 */
const getLastFiveSearchResults = (
  searchResults: ILastSearch[]
): ILastSearch[] => {
  if (searchResults.length > 5) {
    return searchResults.slice(-5);
  }
  return searchResults;
};

/**
 * Сохранит массив с объектами поисковых запросов в localStorage
 */
export const saveLastSearchResultsToLocalStorage = (
  lastSearchResults: ILastSearch[]
): void => {
  saveToLocalStorage({
    last_search: lastSearchResults,
  });
};

export const trackerSearchClickInput = (path: string): void => {
  TM.sendEvent(["LITRES_TRACKER"], {
    eventType: "search_click_input",
    params: {
      source_internal: path,
    },
  });
};

export interface ISpotlightClickData {
  search_id: string;
  query: string;
  row_number: number;
  id: number | string;
  type: string;
}
export interface ISpotlightClickHistory {
  search_id: string;
  query: string | undefined;
}

export const trackerSpotlightClickItem = (
  eventData: ISpotlightClickData,
  e: React.MouseEvent,
  url: string | null,
  id?: number
): void => {
  e.preventDefault();
  const el = (e.target as HTMLElement).closest(".buttonWrapper");
  if (el?.className !== "buttonWrapper") {
    const data = id ? { ...eventData, id } : eventData;
    TM.sendEvent(["LITRES_TRACKER"], {
      eventType: "search_click_spotlight_item",
      params: {
        ...data,
      },
    });
  }
  if (hasWindow) {
    window.location.href = url || "#";
  }
};

export const trackerSpotlightClickButton = (
  eventData: ISpotlightClickData,
  is_purchase_flag: boolean
): void => {
  TM.sendEvent(["LITRES_TRACKER"], {
    eventType: "search_click_spotlight_button",
    params: {
      search_id: eventData.search_id,
      query: eventData.query,
      row_number: eventData.row_number,
      book_id: eventData.id,
      is_purchase_flag,
    },
  });
};

export const formatNumber = (number: number): string => {
  return new Intl.NumberFormat("ru-RU").format(number);
};

export const getDividedNumber = (numberOfBooks: string): string => {
  const parts = numberOfBooks.split(" ");
  const count = formatNumber(Number(parts[0]));
  return count + " " + parts[1];
};

/**
 * Комбинирует историю поиска и подсказки для результатов поиска
 *
 * @param {string} searchValue - Значение для поиска.
 * @param {ILastSearch[]} searchHistory - История поиска.
 * @param {SearchSuggestionSerializer[]} searchSuggestions - Предложения для поиска.
 * @returns {ISearchResultItem[]} - Массив объектов с результатами поиска.
 */
export function getSuggestionSearchResults(
  searchValue: string,
  searchHistory: ILastSearch[],
  searchSuggestions: SearchSuggestionSerializer[]
): ISearchResultItem[] {
  let searchResult: ISearchResultItem[] = [];

  // если поисковый запрос пустой - выводим историю поиска
  if (isEmpty(searchValue)) {
    searchResult = searchHistory
      ?.map(item => ({
        ...item,
        timestamp: item?.timestamp === undefined ? 0 : item.timestamp,
      }))
      .sort((a, b) => b.timestamp - a.timestamp)
      .slice(0, 5)
      .map(sh => ({
        type: "history",
        value: sh.value,
      }));
  }

  //если по запросу пользователя ничего не нашлось в подсказках, показываем его запрос в виде подсказки
  if (searchValue && !searchSuggestions.length) {
    return [{ type: "suggestion", value: searchValue }];
  }

  searchResult = [
    ...searchResult,
    ...(searchSuggestions.map(suggestion => ({
      type: "suggestion",
      value: suggestion.text,
    })) as ISearchResultItem[]),
  ];

  return searchResult;
}
